import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import AnalyticsEmptyState from "../../dashboard/AnalyticsEmptyState";
import { CATEGORICAL, CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../helpers/chartPalette';
import usePieChartLayout from '../../../hooks/usePieChartLayout';

const OTHER_KEY = 'Anders, namelijk..';

const PieChart = ({ data = [], totalResponses }) => {
  // 1) Build rows per moment, carrying "__others" only for the OTHER_KEY slice
  const perMoment = data.flatMap((item) =>
    item.responses.map((response) => ({
      name: response.name,
      value: response.count,
      color: response.color,
      __others: response.name === OTHER_KEY ? (item.others || []) : [],
    }))
  );

  // 2) Aggregate by option name across all moments (sum values, merge others, keep first color)
  const byName = perMoment.reduce((acc, row) => {
    const k = row.name;
    if (!acc[k]) {
      acc[k] = { name: k, value: 0, color: row.color, __others: [] };
    }
    acc[k].value += row.value;
    if (k === OTHER_KEY && Array.isArray(row.__others)) {
      acc[k].__others = acc[k].__others.concat(row.__others);
    }
    // keep the first non-empty color
    if (!acc[k].color && row.color) acc[k].color = row.color;
    return acc;
  }, {});

  // 3) Final pie data
  const aggregated = Object.values(byName);
  // Legenda onder de taart op een smal scherm (gedeelde rapportage op een telefoon).
  const { layout, onResize, ref } = usePieChartLayout(aggregated.length);
  if (aggregated.length === 0) {
    return <AnalyticsEmptyState />;
  }
  const pieData = aggregated;

  // 4) Total for percentage calculation
  const totalValue = pieData.reduce((t, e) => t + (e.value || 0), 0) || 1;

  // 5) Tooltip with free-text list for OTHER_KEY
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const entry = payload[0]?.payload || {};
    const name = entry.name;
    const value = entry.value;
    const pct = ((value / totalValue) * 100).toFixed(2);
    const others = Array.isArray(entry.__others) ? entry.__others : [];
    const showOthers = name === OTHER_KEY && others.length > 0;

    return (
      <div
        style={{
          backgroundColor: 'white',
          padding: 10,
          border: '1px solid #ccc',
          borderRadius: 5,
          maxWidth: 320,
        }}
      >
        <p style={{ margin: 0, fontWeight: 'bold' }}>{name}</p>
        <p style={{ margin: 0 }}>{`Aantal: ${value} (${pct}%)`}</p>

        {showOthers && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600 }}>{OTHER_KEY}</div>
            <ul style={{ paddingLeft: 16, margin: '4px 0' }}>
              {others.slice(0, 5).map((txt, i) => (
                <li key={`other-${i}`}>{String(txt)}</li>
              ))}
            </ul>
            {others.length > 5 && (
              <div style={{ fontStyle: 'italic' }}>
                +{others.length - 5} meer…
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // 6) Custom Legend showing counts
  const renderCustomLegend = (props) => {
    const { payload } = props || {};
    if (!payload) return null;
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {payload.map((entry, index) => (
          <li
            key={`legend-item-${index}`}
            style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: entry.color,
                marginRight: 8,
              }}
            />
            <span>{`${entry.payload.name} (${entry.payload.value})`}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div ref={ref} style={{ display: 'flex', justifyContent: 'center', flex: 1, minHeight: 0, width: '100%' }}>
      <ResponsiveContainer width="100%" height={layout.height} onResize={onResize}>
        <RechartsPieChart>
          <Pie
            data={pieData}
            cx={layout.cx}
            cy="50%"
            innerRadius={0}
            outerRadius={layout.outerRadius}
            paddingAngle={0}
            dataKey="value"
            labelLine={false}
          >
            {pieData.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={entry.color || CATEGORICAL[0]} stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            content={renderCustomLegend}
            layout={layout.legend.layout}
            align={layout.legend.align}
            verticalAlign={layout.legend.verticalAlign}
            right="25px"
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChart;


  
  